// campaign-clock-transport.ts — the ONE way any feature starts or stops campaign time.
//
// Written 2026-09-08 out of a bug: three separate features each reached for
// `document.getElementById('pause_campaign_time').style.display === 'block'` as their test for
// "is the clock running", and each silently did nothing when that string was anything else. It is
// anything else more often than it looks:
//   • in-combat chrome (syncMyCampaignTimeControlsToEncounterInCombat) sets BOTH transport buttons
//     to display:none and hands the slot to #btn_in_combat_time_control — while the derived clock
//     may still be running, because it was started out of combat;
//   • the truth-driven mirror (ftClockSyncGmControls) writes '' for a shown PLAY button, so the
//     symmetric guard for "start time" could never have fired at all.
// The FT3D GM-controls Hard Pause was dead in exactly that state: start time, roll initiative, and
// the pause button stopped being able to stop anything while the heartbeat kept replaying.
//
// So: ask the clock, not the DOM. window.ftCampaignClock is the authority in the derived lane — it
// owns the optimistic local transition (applied SYNCHRONOUSLY, which is why the did-it-move answer
// below is trustworthy), the checkpoint timers and the server write. The DOM buttons are the state
// only in the legacy lane (FT_CLOCK_DERIVED=off), and there `display === 'block'` genuinely IS the
// truth, because my_campaign.php's 6 s tick reads that same string.

interface FtCampaignClockApi {
    isDerived?: () => boolean;
    isRunning?: () => boolean;
    play?: () => Promise<unknown>;
    pause?: () => Promise<unknown>;
}

function clockApi(): FtCampaignClockApi | undefined {
    return (window as unknown as Record<string, unknown>).ftCampaignClock as FtCampaignClockApi | undefined;
}

function derivedLane(clock: FtCampaignClockApi | undefined): clock is FtCampaignClockApi {
    return typeof clock?.isDerived === 'function' && clock.isDerived() === true;
}

/** Is campaign time advancing right now? Authority first, the legacy buttons only as fallback. */
export function ftCampaignClockIsRunning(): boolean {
    const clock = clockApi();
    if (derivedLane(clock)) return clock.isRunning?.() === true;
    return (document.getElementById('pause_campaign_time') as HTMLElement | null)?.style.display === 'block';
}

export interface FtSetCampaignClockOptions {
    /**
     * Issue the command even when we already believe the clock is in the requested state.
     * For an explicit GM gesture this is the right call: a locally-stale "stopped" must not swallow
     * a pause of a clock the server still has running, and both server actions are idempotent (play
     * folds the elapsed span before re-basing; pause on a stopped clock answers no-op). Automatic
     * callers leave it off, so they can report honestly whether THEY are what stopped the clock.
     */
    force?: boolean;
}

/**
 * Start (`true`) or stop (`false`) campaign time.
 * @returns true only when this call is what moved the OPTIMISTIC LOCAL state — which is the answer an
 *          automatic pause needs before it tells a GM "campaign time paused". It is not a server
 *          receipt. Two consequences worth knowing (Codex review 2026-09-08):
 *
 * ⚠ A `true` cannot be taken back. The local transition lands synchronously, before the fetch; a later
 *   rollback (network failure on a pause, or the server refusing a PLAY because the loaded encounter is
 *   in combat — it warns through the clock runtime) cannot un-return it. Nothing renders GM-facing text
 *   from a play's answer today; if that changes, confirm against the clock rather than this boolean.
 * ⚠ Under `force`, a `false` does NOT mean failure — it means the local state already matched, while a
 *   command was still issued. Never branch on it as an error in a forced call.
 *
 * ⛔ The derived lane deliberately does NOT fire window.broadcastCampaignTransport the way clicking the
 * real button does. That legacy player-facing mirror decides "is it running" from the very
 * #pause_campaign_time display string this module exists to stop trusting, so firing it here would
 * broadcast a wrong value in exactly the states we just fixed. In the derived lane the campaign blob's
 * running_since is the transport truth (ftClockSyncPlayerTransport), and the clock write publishes it.
 */
export function ftSetCampaignClockRunning(shouldRun: boolean, options: FtSetCampaignClockOptions = {}): boolean {
    const clock = clockApi();

    if (derivedLane(clock)) {
        const before = clock.isRunning?.() === true;
        if (before === shouldRun && !options.force) return false;
        const request = shouldRun ? clock.play : clock.pause;
        if (typeof request !== 'function') return false;
        // The optimistic transition lands before the promise; the answer (or its rollback) follows.
        void Promise.resolve(request.call(clock)).catch(() => { /* the next checkpoint re-bases */ });
        return before !== shouldRun && clock.isRunning?.() === shouldRun;
    }

    // Legacy lane: the my_campaign.php buttons ARE the state, and their click handlers do the whole
    // job (display, spinner, the player-facing transport mirror). A synthetic click fires a handler
    // on a hidden button too, so nothing here tests visibility — only the state string.
    const running = (document.getElementById('pause_campaign_time') as HTMLElement | null)?.style.display === 'block';
    if (running === shouldRun && !options.force) return false;
    const button = document.getElementById(
        shouldRun ? 'play_campaign_time' : 'pause_campaign_time',
    ) as HTMLButtonElement | null;
    if (!button) return false;
    try {
        button.click();
    } catch {
        return false;
    }
    return running !== shouldRun && ftCampaignClockIsRunning() === shouldRun;
}
